import { useCallback } from 'react';

import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import * as customerApi from 'api/customerApi';
import {
  contractRegistrationActions,
  contractRegistrationSelectors,
} from 'store/reducers/customerReducer/contractRegistrationSlice';
import {
  contractListActions,
  contractListSelectors,
} from 'store/reducers/customerReducer/contractListSlice';
import {
  listActions,
  listSelectors,
} from 'store/reducers/customerReducer/listSlice';
import { NewContract } from 'types/contract';
import { useToast } from 'hooks/useToast';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { ContractFormFields } from '../types/contractFormField';

/**
 * 顧客登録 store の実装をコンポーネントから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useContractRegistrationStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const isOpenInputModal = useAppSelector(
    contractRegistrationSelectors.isOpenInputModal,
  );
  const isOpenConfirmationModal = useAppSelector(
    contractRegistrationSelectors.isOpenConfirmationModal,
  );

  const menuId = useAppSelector(contractRegistrationSelectors.menuId);

  const startDate = useAppSelector(contractRegistrationSelectors.startDate);
  const endDate = useAppSelector(contractRegistrationSelectors.endDate);

  const displayStartDate = useAppSelector(
    contractRegistrationSelectors.displayStartDate,
  );
  const displayEndDate = useAppSelector(
    contractRegistrationSelectors.displayEndDate,
  );

  const selectedCustomer = useAppSelector(listSelectors.selectedCustomer);
  const menuMap = useAppSelector(contractListSelectors.menuMap);
  const menuName = menuId ? menuMap[menuId].name ?? '' : '';

  const init = useCallback(() => {
    dispatch(contractRegistrationActions.init());
  }, [dispatch]);

  const openModal = useCallback(() => {
    dispatch(contractRegistrationActions.modalOpened());
  }, [dispatch]);

  const goInputModal = useCallback(() => {
    dispatch(contractRegistrationActions.stepMovedToInput());
  }, [dispatch]);

  const goConfirmationModal = useCallback(() => {
    dispatch(contractRegistrationActions.stepMovedToConfirmation());
  }, [dispatch]);

  const setForm = useCallback(
    (form: ContractFormFields) => {
      dispatch(
        contractRegistrationActions.formGroupUpdated({
          form,
        }),
      );
    },
    [dispatch],
  );

  const changeStartDate = useCallback(
    (date: string) => {
      dispatch(
        contractRegistrationActions.startDateChanged({
          startDate: date,
        }),
      );
    },
    [dispatch],
  );

  const changeEndDate = useCallback(
    (date: string) => {
      dispatch(
        contractRegistrationActions.endDateChanged({
          endDate: date,
        }),
      );
    },
    [dispatch],
  );

  const postContractAndReload = useCallback(async () => {
    if (!menuId) {
      return;
    }
    if (!selectedCustomer) {
      return;
    }

    dispatch(loaderActions.setLoading(true));
    dispatch(listActions.loadStarted());
    const params: NewContract = {
      menuId,
      customerId: selectedCustomer.id,
      startDate,
      endDate,
    };
    const result = await customerApi.postContract(selectedCustomer.id, params);
    dispatch(listActions.loadEnded());
    if (result.ok) {
      // 顧客一覧をリロードするために最終更新日を更新する
      dispatch(
        contractListActions.finalUpdateTimeUpdated({
          finalUpdateTime: new Date().toISOString(),
        }),
      );
      toast({
        status: 'success',
        description: '契約メニューの作成に成功しました。',
      });
    } else {
      toast({
        status: 'error',
        description: `契約メニューの作成に失敗しました: ${result.err.message}`,
      });
    }
    dispatch(loaderActions.setLoading(false));
  }, [menuId, selectedCustomer, startDate, endDate, dispatch, toast]);

  return {
    isOpenInputModal,
    isOpenConfirmationModal,
    menuId,
    menuName,
    startDate,
    endDate,
    displayStartDate,
    displayEndDate,
    init,
    openModal,
    goInputModal,
    goConfirmationModal,
    setForm,
    changeStartDate,
    changeEndDate,
    postContractAndReload,
  };
};
